import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi, formatApiError } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authApi.me();
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await authApi.login(email, password);
      // Fallback for cross-domain cookie issues
      try {
        if (data.access_token) {
          localStorage.setItem('gd-access-token', data.access_token);
        }
      } catch {}
      setUser(data.user);
      return { ok: true, user: data.user, must_change_password: data.must_change_password };
    } catch (err) {
      return { ok: false, error: formatApiError(err) };
    }
  }, []);

  const signOut = useCallback(async () => {
    // 1. Clear client-side artifacts FIRST (defense in depth)
    try { localStorage.removeItem('gd-access-token'); } catch {}
    try {
      // Try to expire cookies client-side too (may fail for httpOnly but harmless)
      document.cookie = 'access_token=; Max-Age=0; path=/; SameSite=None; Secure';
      document.cookie = 'refresh_token=; Max-Age=0; path=/; SameSite=None; Secure';
    } catch {}
    // 2. Then ask backend to invalidate
    try { await authApi.logout(); } catch {}
    setUser(null);
  }, []);

  const value = {
    user,
    profile: user,
    role: user?.role || null,
    loading,
    login,
    signOut,
    refreshProfile: refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
